import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import { customerApi } from '../../services/customerAuth'
import { useLanguage } from '../../context/LanguageContext'

function fmtDate(d) {
  if (!d) return ''
  try { return new Date(d).toLocaleDateString('en-US', { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' }) }
  catch { return String(d) }
}

export default function PayPage() {
  const { t } = useLanguage()
  const { token } = useParams()
  const [state, setState] = useState({ phase: 'loading', data: null, error: null })
  const [card, setCard] = useState({ number: '', expiry: '', cvc: '' })
  const [submitting, setSubmitting] = useState(false)

  useEffect(() => {
    customerApi.get(`/pay/${token}`)
      .then(({ data }) => {
        if (data.alreadyPaid) return setState({ phase: 'already_paid', data })
        if (data.cancelled) return setState({ phase: 'cancelled', data })
        if (data.expired) return setState({ phase: 'expired', data })
        setState({ phase: 'ready', data })
      })
      .catch(err => setState({ phase: 'error', error: err.response?.data?.message || err.message }))
  }, [token])

  async function submit(e) {
    e.preventDefault()
    setSubmitting(true)
    try {
      const { data } = await customerApi.post(`/pay/${token}`, { card })
      setState(s => ({ ...s, phase: 'paid_just_now', data: { ...s.data, transactionId: data.transactionId, amount: data.amount } }))
    } catch (err) {
      const msg = err.response?.data?.message || t('sp_payment_failed', 'Payment failed')
      const decline = err.response?.data?.declineCode
      alert(msg + (decline ? ` (code: ${decline})` : ''))
    } finally { setSubmitting(false) }
  }

  return (
    <div style={{ minHeight: '100vh', background: '#f8fafc', fontFamily: "'Plus Jakarta Sans', system-ui, sans-serif" }}>
      <div style={{ background: '#153230', color: '#fff', padding: '18px 20px' }}>
        <div style={{ maxWidth: 560, margin: '0 auto', fontWeight: 800, fontSize: 18 }}>{t('sp_playstay_secure_payment', 'PlayStay · Secure Payment')}</div>
      </div>

      <div style={{ maxWidth: 560, margin: '0 auto', padding: '24px 16px 40px' }}>
        {state.phase === 'loading' && <Card><div style={{ textAlign: 'center', color: '#64748b' }}>{t('sp_loading_short', 'Loading…')}</div></Card>}

        {state.phase === 'error' && (
          <Card><h2 style={{ color: '#991b1b' }}>{t('sp_cant_load_payment', 'Can\'t load this payment')}</h2><p style={{ color: '#555' }}>{state.error}</p></Card>
        )}

        {state.phase === 'already_paid' && (
          <Card>
            <h2 style={{ color: '#153230', margin: '0 0 8px' }}>{t('sp_already_paid', 'Already paid')}</h2>
            <p style={{ color: '#475569' }}>{t('sp_already_paid_blurb1', 'Your share of')} <strong>${Number(state.data.amount).toFixed(2)}</strong> {t('sp_was_paid_on', 'was paid on')} {fmtDate(state.data.paidAt)}.</p>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 16 }}>{t('sp_no_further_action', 'No further action needed. The primary booker has been notified.')}</p>
          </Card>
        )}

        {state.phase === 'cancelled' && (
          <Card>
            <h2 style={{ color: '#991b1b', margin: '0 0 8px' }}>{t('sp_payment_cancelled', 'Payment cancelled')}</h2>
            <p style={{ color: '#555' }}>{t('sp_payment_link_cancelled_blurb', 'This payment link has been cancelled. Reason:')} {state.data.reason || t('sp_unknown', 'unknown')}.</p>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 16 }}>{t('sp_contact_primary_booker', 'Please contact the primary booker if you still wish to join the trip.')}</p>
          </Card>
        )}

        {state.phase === 'expired' && (
          <Card>
            <h2 style={{ color: '#991b1b', margin: '0 0 8px' }}>{t('sp_payment_link_expired', 'Payment link expired')}</h2>
            <p style={{ color: '#555' }}>{t('sp_payment_deadline_passed', 'The deadline on this payment link has passed. The booking has been handled per the primary\'s decision.')}</p>
          </Card>
        )}

        {state.phase === 'paid_just_now' && (
          <Card>
            <h2 style={{ color: '#153230', margin: '0 0 8px' }}>{t('sp_thanks_payment_received', 'Thanks — payment received')}</h2>
            <p style={{ color: '#475569' }}>{t('sp_we_charged', 'We charged')} <strong>${Number(state.data.amount).toFixed(2)}</strong> {t('sp_to_your_card', 'to your card. A receipt has been sent to your email.')}</p>
            <p style={{ fontSize: 13, color: '#64748b', marginTop: 16 }}>
              {t('sp_transaction_id', 'Transaction ID:')} <code style={{ background: '#f0fdf4', padding: '2px 6px', borderRadius: 4 }}>{state.data.transactionId}</code>
            </p>
          </Card>
        )}

        {state.phase === 'ready' && state.data && (
          <>
            <Card>
              <div style={{ fontSize: 22, fontWeight: 800, color: '#153230', marginBottom: 4 }}>
                ${Number(state.data.amount).toFixed(2)}
              </div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{t('sp_your_share', 'Your share of the Stay & Play trip')}</div>
              {state.data.installmentTotal > 1 && (
                <div style={{ fontSize: 12, color: '#92400e', marginTop: 8, padding: '4px 10px', background: '#fef3c7', borderRadius: 4, display: 'inline-block' }}>
                  {t('sp_installment', 'Installment')} {state.data.installmentIndex} {t('of', 'of')} {state.data.installmentTotal}
                </div>
              )}
              {state.data.dueDate && (
                <div style={{ fontSize: 12, color: '#92400e', marginTop: 8 }}>
                  {t('sp_due_by', 'Due by')} <strong>{fmtDate(state.data.dueDate)}</strong>
                </div>
              )}
            </Card>

            <Card>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 10 }}>{t('sp_trip_details', 'Trip details')}</div>
              <div style={{ fontSize: 14, color: '#0f172a', marginBottom: 4 }}>{state.data.trip.packageName}</div>
              <div style={{ fontSize: 13, color: '#64748b' }}>{state.data.trip.courseName}</div>
              <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>{t('sp_tee_date', 'Tee date:')} {fmtDate(state.data.trip.playDate)}</div>
              {state.data.trip.hotelName && (
                <div style={{ fontSize: 13, color: '#64748b', marginTop: 4 }}>
                  {t('sp_th_hotel', 'Hotel')}: {state.data.trip.hotelName} ({fmtDate(state.data.trip.hotelCheckin)} – {fmtDate(state.data.trip.hotelCheckout)})
                </div>
              )}
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 8, fontStyle: 'italic' }}>
                {t('sp_booked_by', 'Booked by')} {state.data.trip.primaryGolferName}
              </div>
            </Card>

            <Card>
              <div style={{ fontSize: 14, fontWeight: 700, color: '#153230', marginBottom: 12 }}>{t('sp_payment_method', 'Payment method')}</div>
              <form onSubmit={submit}>
                <label style={labelStyle}>{t('sp_card_number', 'Card number')}</label>
                <input required type="text" placeholder="1234 5678 9012 3456" maxLength={19}
                  value={card.number} onChange={e => setCard(c => ({ ...c, number: e.target.value }))} style={inputStyle} />
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 10 }}>
                  <div>
                    <label style={labelStyle}>{t('sp_expiry', 'Expiry')}</label>
                    <input required type="text" placeholder="MM/YY" maxLength={5}
                      value={card.expiry} onChange={e => setCard(c => ({ ...c, expiry: e.target.value }))} style={inputStyle} />
                  </div>
                  <div>
                    <label style={labelStyle}>{t('sp_cvc', 'CVC')}</label>
                    <input required type="text" placeholder="123" maxLength={4}
                      value={card.cvc} onChange={e => setCard(c => ({ ...c, cvc: e.target.value }))} style={inputStyle} />
                  </div>
                </div>
                <button type="submit" disabled={submitting} style={submitBtn}>
                  {submitting ? t('sp_processing', 'Processing…') : `${t('sp_pay', 'Pay')} $${Number(state.data.amount).toFixed(2)}`}
                </button>
                <div style={{ fontSize: 11, color: '#94a3b8', textAlign: 'center', marginTop: 10 }}>
                  {t('sp_secured_by_integolf', 'Secured by InteGolf. Your card details are transmitted over HTTPS and processed through Moneris.')}
                </div>
              </form>
            </Card>
          </>
        )}
      </div>
    </div>
  )
}

function Card({ children }) {
  return <div style={{ background: '#fff', padding: 20, borderRadius: 12, marginBottom: 16, border: '1px solid #e5e7eb', boxShadow: '0 1px 3px rgba(0,0,0,0.06)' }}>{children}</div>
}

const labelStyle = { display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', margin: '8px 0 4px' }
const inputStyle = { width: '100%', padding: '10px 12px', border: '1px solid #cbd5e1', borderRadius: 6, fontSize: 14, boxSizing: 'border-box', fontFamily: 'inherit', marginBottom: 4 }
const submitBtn = { width: '100%', padding: '14px', background: '#00d19f', color: '#153230', border: 'none', borderRadius: 6, fontWeight: 700, fontSize: 16, cursor: 'pointer', marginTop: 16 }
